import React from "react"
import { graphql, Link } from "gatsby"
import Layout from "../components/shared/layout"
import SEO from "../components/SEO/SEO"
import styled from "@emotion/styled"
import {
  wrapper,
  wrapper1200,
  featuredWrapper,
  noFeaturedWrapper,
  color,
  breakpoints,
  ContentStyles,
  ContentStylesExt,
} from "../utils/style"
import ButtonLink from "../components/shared/ButtonLink"
import Img from "gatsby-image"
import Breadcrumbs from "../components/shared/Breadcrumbs"
import PromoBlock from "../components/Content/PromoBlock"
import GameProvidersNew from "../components/Content/GameProvidersNew"
import ContactBlock from "../components/Content/ContactBlock"

const WrapperDesktop = styled("div")`
  ${wrapper1200};
`

const FeaturedWrapper = styled("div")`
  ${featuredWrapper};
  position: relative;
  overflow: hidden;
  border: 4px solid ${color.red};
  border-radius: 8px;
  box-shadow: 0 0 48px rgba(142, 0, 0, 0.6);
  .gatsby-image-wrapper {
    border-radius: 8px;
    max-height: 228px;
    filter: drop-shadow(0px 0px 48px rgba(142, 0, 0, 0.6));
    z-index: 1;
    &::after {
      content: "";
      width: 100%;
      height: 100%;
      background: rgba(104, 0, 0, 0.3);
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      margin: 0 auto;
      border-radius: 8px;
      z-index: 2;
    }
  }
  h1 {
    font-size: 1.8rem;
    line-height: 2.3rem;
    text-align: center;
    text-shadow: ${color.goldTextShadow};
    max-width: 60%;
    position: absolute;
    top: 30%;
    left: 0;
    right: 0;
    margin: 0 auto;
    z-index: 3;
  }
  @media (max-width: ${breakpoints.md}) {
    h1 {
      font-size: 26px;
      line-height: 32px;
    }
  }
  @media (max-width: ${breakpoints.sm}) {
    .gatsby-image-wrapper {
      max-height: 100%;
    }
    h1 {
      max-width: 90%;
      top: 20%;
      font-size: 18px;
      line-height: 26px;
    }
  }
`

const NoFeaturedWrapper = styled("div")`
  ${noFeaturedWrapper};
  h1 {
    font-size: 1.8rem;
    line-height: 2.3rem;
  }
  @media (max-width: ${breakpoints.sm}) {
    h1 {
      font-size: 18px;
      line-height: 26px;
    }
  }
`

const ContentWrapper = styled("div")`
  ${wrapper};
  ${ContentStyles};
  ${ContentStylesExt};
  padding: 0;
  .prev-next {
    min-height: 70px;
    padding-bottom: 20px;
  }
  .prev-button {
    border-width: 2px;
  }
  .next-button {
    border-width: 2px;
    float: right;
  }
  a.tag {
    color: ${color.gold};
  }
`

const Bg = styled("div")`
  border-top: 1px solid ${color.transparentWhite1};
  padding: 15px 0 5px;
`
const RelatedWrapper = styled("div")`
  margin: 20px 0;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  a {
    display: flex;
    flex-basis: 49%;
    position: relative;
    margin: 10px 0;
    overflow: hidden;

    .image-wrap {
      flex-basis: 40%;
    }

    .text-part {
      flex-basis: 60%;
      padding: 0 20px;
    }

    .h5 {
      display: block;
      margin: 0;
      font-size: 18px;
      line-height: 24px;
      color: ${color.white};
    }

    .button {
      margin-top: 8px;
    }

    &:hover,
    &:active {
      text-decoration: none;
      color: ${color.white};
    }
    .gatsby-image-wrapper {
      height: 100%;
    }
  }

  @media (max-width: ${breakpoints.md}) {
    a {
      flex-basis: 100%;
    }
  }

  @media (max-width: ${breakpoints.sm}) {
    width: 100%;
    a {
      display: block;
      .text-part {
        padding: 16px 0;
      }
    }
  }
`

const PostTemplate = ({ data, pageContext }) => {
  const post = data.wordpressPost
  const metaTitle =
    post.yoast_meta && post.yoast_meta.yoast_wpseo_title
      ? post.yoast_meta.yoast_wpseo_title
      : post.title + +"- " + process.env.OWNER
  const metaDesc =
    post.yoast_meta && post.yoast_meta.yoast_wpseo_metadesc
      ? post.yoast_meta.yoast_wpseo_metadesc
      : ""
  const relatedPosts = data.allWordpressPost
  const relatedIds = pageContext.related
  const breadcrumbs = pageContext.breadcrumbs
  const page_title = pageContext.page_title
  const prevPost = pageContext.prev
  const nextPost = pageContext.next
  const categories = post?.categories
  const tags = post?.tags
  const socialImage = post.featured_media?.localFile?.publicURL

  return (
    <Layout>
      <SEO
        title={metaTitle}
        description={metaDesc}
        pathname={post.path}
        key="seo"
        breadcrumbs={breadcrumbs}
        socialImage={socialImage}
      />
      <WrapperDesktop>
        <Breadcrumbs breadcrumbs={breadcrumbs} page_title={page_title} />
        {post.featured_media &&
        post.featured_media.localFile &&
        post.featured_media.localFile.childImageSharp &&
        post.featured_media.localFile.childImageSharp.fluid ? (
          <FeaturedWrapper key="featured-wrapper">
            <Img
              fluid={post.featured_media.localFile.childImageSharp.fluid}
              alt={post.featured_media.alt_text}
            />
            <h1
              className="h1 rich-design"
              dangerouslySetInnerHTML={{ __html: post.title }}
            />
          </FeaturedWrapper>
        ) : (
          <NoFeaturedWrapper key="no-featured-wrapper">
            <h1
              className="h1 rich-design"
              dangerouslySetInnerHTML={{ __html: post.title }}
            />
          </NoFeaturedWrapper>
        )}

        <ContactBlock />

        <ContentWrapper key="content-wrapper">
          <div
            key="content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {categories?.map((item, index) => {
            return (
              <div key={index + Math.random() * 100}>
                {item.name === "Blog" && (
                  <div className="tags">
                    <p>
                      <span>Tagged:</span>{" "}
                      {tags?.map((item, index) => {
                        return (
                          <>
                            <Link
                              key={index + Math.random() * 100}
                              to={item.path}
                              className="tag"
                            >
                              {item.name}
                            </Link>
                            {tags.length !== index + 1 && <>, </>}
                          </>
                        )
                      })}
                    </p>
                  </div>
                )}
              </div>
            )
          })}

          {(prevPost.slug || nextPost.slug) && (
            <div className="prev-next">
              {prevPost.slug &&
                !prevPost.slug.includes("provider-carousel") && (
                  <ButtonLink
                    className="button transparent-button prev-button"
                    iconName="arrow-left"
                    iconLeft={true}
                    iconColor={color.white}
                    to={prevPost.slug + "/"}
                    text="ก่อนหน้า"
                  />
                )}
              {nextPost.slug &&
                !nextPost.slug.includes("provider-carousel") && (
                  <ButtonLink
                    className="button transparent-button next-button"
                    to={nextPost.slug + "/"}
                    iconColor={color.white}
                    text="ต่อไป"
                  />
                )}
            </div>
          )}
        </ContentWrapper>

        {relatedIds.length > 0 && (
          <Bg key="related-bg">
            <RelatedWrapper>
              {relatedPosts.edges.map(({ node }) => (
                <Link key={node.slug} to={"/" + node.slug + "/"}>
                  <div className="image-wrap">
                    {node.featured_media &&
                    node.featured_media.localFile &&
                    node.featured_media.localFile.childImageSharp &&
                    node.featured_media.localFile.childImageSharp.fluid ? (
                      <Img
                        fluid={
                          node.featured_media.localFile.childImageSharp.fluid
                        }
                        alt={
                          node.featured_media.alt_text
                            ? node.featured_media.alt_text
                            : node.title
                        }
                      />
                    ) : (
                      <Img
                        fluid={data.placeholder.childImageSharp.fluid}
                        alt={node.title}
                      />
                    )}
                  </div>
                  <div className="text-part">
                    <span className="h5">{node.title}</span>
                    <div
                      className="excerpt"
                      dangerouslySetInnerHTML={{
                        __html: node.excerpt.slice(0, 250) + "...",
                      }}
                    />
                    <span className="button transparent-button-gold">
                      อ่านเพิ่มเติม
                    </span>
                  </div>
                </Link>
              ))}
            </RelatedWrapper>
          </Bg>
        )}
      </WrapperDesktop>

      <GameProvidersNew />

      <WrapperDesktop>
        <PromoBlock />
      </WrapperDesktop>
    </Layout>
  )
}

export default PostTemplate

export const pageQuery = graphql`
  query($id: String!, $related: [Int]) {
    wordpressPost(id: { eq: $id }) {
      wordpress_id
      title
      content
      path
      date(formatString: "MMMM DD, YYYY")
      categories {
        name
      }
      tags {
        name
        path
      }
      yoast_meta {
        yoast_wpseo_title
        yoast_wpseo_metadesc
      }
      featured_media {
        alt_text
        localFile {
          publicURL
          childImageSharp {
            fluid(maxWidth: 1224) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    allWordpressPost(
      filter: {
        wordpress_id: { in: $related }
        acf: { post_template: { ne: "service_post_carousel" } }
      }
      limit: 2
    ) {
      edges {
        node {
          slug
          title
          excerpt
          featured_media {
            alt_text
            localFile {
              childImageSharp {
                fluid(maxWidth: 350) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    }
    placeholder: file(relativePath: { eq: "placeholder.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 350) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`
